import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit'
import { ReactNode } from 'react'

export interface Crumb {
  label: string
  link: string
}

export interface UserScreening {
  user_id: string
  showScreening: boolean
  isFlagged: boolean
  name: string
}

export interface Screening {
  date: Date
  users: UserScreening[]
}

export interface AppState {
  tenant: string | null
  dialog: Dialog | null
  toast: Notification | null
  dialogProcessing: boolean
  formProcessing: boolean
  temporaryClosedDialogs: any
  minimizeDialogs: Dialog[] | any
  crumbs: Crumb[]
  isSidebarVisible: boolean
  opened: string
  isUploading: boolean
  mobileMenu: MobileMenu | null

  isScreeningRequested: boolean
  screening: Screening

  drawer: Drawer | null
  drawerProcessing: boolean
  temporaryClosedDrawers: any

  refetchPage: string | null
}

export interface Dialog {
  minimizeName?: string
  component: Function
  props: object | any
  id?: string
}

export interface Drawer {
  minimizeName?: string
  component: Function
  props: object | any
}

export interface MobileMenu {
  component: Function
  props: object | any
  id?: string
}

export interface Notification {
  type: 'error' | 'success' | 'warning'
  message: string | ReactNode
  messageBody?: ReactNode
  subText?: string
  showSubText?: boolean
  isSubTextHTML?: boolean
  autohideTimeout?: number
  onDismiss?(): void
}

const initialState: AppState = {
  tenant: null,
  dialog: null,
  toast: null,
  dialogProcessing: false,
  formProcessing: false,
  temporaryClosedDialogs: [],
  minimizeDialogs: [],
  crumbs: [],
  isSidebarVisible: true,
  isUploading: false,
  opened: '/participants',
  mobileMenu: null,

  isScreeningRequested: false,
  screening: {
    date: new Date(),
    users: [],
  },

  drawer: null,
  drawerProcessing: false,
  temporaryClosedDrawers: [],

  refetchPage: null,
}

// shared logic
function hideDialogAction(state: AppState) {
  let dialog = null
  let { temporaryClosedDialogs } = state
  if (temporaryClosedDialogs.length) {
    ;[dialog] = [...temporaryClosedDialogs].reverse()
    temporaryClosedDialogs = temporaryClosedDialogs.slice(0, temporaryClosedDialogs.length - 1)
  }
  state.dialog = dialog
  state.dialogProcessing = false
  state.temporaryClosedDialogs = temporaryClosedDialogs
}

// shared logic
function hideDrawerAction(state: AppState) {
  let drawer = null
  let { temporaryClosedDrawers } = state
  if (temporaryClosedDrawers.length) {
    ;[drawer] = [...temporaryClosedDrawers].reverse()
    temporaryClosedDrawers = temporaryClosedDrawers.slice(0, temporaryClosedDrawers.length - 1)
  }
  state.drawer = drawer
  state.drawerProcessing = false
  state.temporaryClosedDrawers = temporaryClosedDrawers
}

const app = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateDialogProps(state, { payload }: PayloadAction<{ props: object }>) {
      if (state.dialog) {
        state.dialog.props = {
          ...state.dialog.props,
          ...payload.props,
        }
      }
    },
    updateDrawerProps(state, { payload }: PayloadAction<{ props: object }>) {
      if (state.drawer) {
        state.drawer.props = {
          ...state.drawer.props,
          ...payload.props,
        }
      }
    },
    minimizeDialog(state, { payload }) {
      let { dialog, temporaryClosedDialogs, minimizeDialogs } = state

      if (dialog) {
        minimizeDialogs = [...minimizeDialogs, { ...dialog, minimizeName: payload }]
      }

      if (temporaryClosedDialogs.length) {
        ;[dialog] = [...temporaryClosedDialogs].reverse()
        temporaryClosedDialogs = temporaryClosedDialogs.slice(0, temporaryClosedDialogs.length - 1)
      } else {
        dialog = null
      }

      state.dialog = dialog
      state.minimizeDialogs = minimizeDialogs
    },
    showMinimizeDialog(state, { payload }) {
      const { dialog } = state
      let { temporaryClosedDialogs, minimizeDialogs } = state

      if (minimizeDialogs.length && payload) {
        const minimizeIndex = minimizeDialogs.findIndex((each: any) => each.minimizeName === payload)
        if (minimizeIndex !== -1) {
          state.dialog = minimizeDialogs[minimizeIndex]
          minimizeDialogs = minimizeDialogs.filter((e: any, index: number) => index !== minimizeIndex)

          if (dialog) {
            temporaryClosedDialogs = [...temporaryClosedDialogs, dialog]
          }
        }
      }

      state.minimizeDialogs = minimizeDialogs
      state.temporaryClosedDialogs = temporaryClosedDialogs
    },
    showDialog(state, { payload }: PayloadAction<Dialog>) {
      const { dialog } = state
      let { temporaryClosedDialogs, minimizeDialogs } = state
      if (dialog) {
        temporaryClosedDialogs = [...temporaryClosedDialogs, dialog]
      }

      if (minimizeDialogs.length && payload.props.title) {
        const minimizeIndex = minimizeDialogs.findIndex((each: any) => each.minimizeName === payload.props.title)
        if (minimizeIndex !== -1) {
          state.dialog = minimizeDialogs[minimizeIndex]
          minimizeDialogs = minimizeDialogs.filter((e: any, index: number) => index !== minimizeIndex)
        } else {
          state.dialog = payload
        }
      } else {
        state.dialog = payload
      }

      state.minimizeDialogs = minimizeDialogs
      state.temporaryClosedDialogs = temporaryClosedDialogs
    },
    showDrawer(state, { payload }: PayloadAction<Drawer>) {
      const { drawer } = state
      let { temporaryClosedDrawers } = state
      if (drawer) {
        temporaryClosedDrawers = [...temporaryClosedDrawers, drawer]
      }

      state.drawer = payload
      state.temporaryClosedDrawers = temporaryClosedDrawers
    },
    hideAllDialogs(state) {
      state.dialog = null
      state.temporaryClosedDialogs = []
      state.minimizeDialogs = []
      state.dialogProcessing = false
    },
    hideAllDrawers(state) {
      state.drawer = null
      state.temporaryClosedDrawers = []
      state.drawerProcessing = false
    },
    hideDialog: hideDialogAction,
    hideDrawer: hideDrawerAction,
    dialogProcessing(state, action: PayloadAction<boolean>) {
      const { dialog } = state
      if (dialog && action.payload) {
        state.dialogProcessing = action.payload
      } else if (!action.payload) {
        state.dialogProcessing = action.payload
      }
    },
    drawerProcessing(state, action: PayloadAction<boolean>) {
      const { drawer } = state
      if (drawer && action.payload) {
        state.drawerProcessing = action.payload
      } else if (!action.payload) {
        state.drawerProcessing = action.payload
      }
    },
    formProcessing(state, action: PayloadAction<boolean>) {
      state.formProcessing = action.payload
    },
    hideNotification(state) {
      state.toast = null
    },
    showWarning(
      state,
      { payload }: PayloadAction<{ message: string | ReactNode; subText?: string; showSubText?: boolean }>,
    ) {
      state.toast = {
        ...payload,
        type: 'warning',
      }
      state.formProcessing = false
      state.dialogProcessing = false
      state.drawerProcessing = false
    },
    showError(
      state,
      { payload }: PayloadAction<{ message: string | ReactNode; subText?: string; showSubText?: boolean }>,
    ) {
      state.toast = {
        ...payload,
        type: 'error',
      }
      state.formProcessing = false
      state.dialogProcessing = false
      state.drawerProcessing = false
    },
    showSuccess(
      state,
      {
        payload,
      }: PayloadAction<{
        message: string | ReactNode
        messageBody?: ReactNode
        subText?: string
        hideDialog?: boolean
        hideDrawer?: boolean
        autohideTimeout?: number
        onDismiss?: () => void
      }>,
    ) {
      const {
        message,
        messageBody,
        subText,
        hideDialog = true,
        hideDrawer = false,
        autohideTimeout,
        onDismiss,
      } = payload
      if (hideDialog) {
        hideDialogAction(state)
      }

      if (hideDrawer) {
        hideDrawerAction(state)
      }

      // state.dialogProcessing = false
      state.toast = {
        type: 'success',
        message,
        messageBody,
        subText,
        autohideTimeout,
        onDismiss,
      }
    },
    clearLoading(state) {
      state.formProcessing = false
      state.dialogProcessing = false
      state.drawerProcessing = false
    },
    setIsUploading(state, { payload }: PayloadAction<boolean>) {
      state.isUploading = payload
    },
    setCrumbs(state, { payload }) {
      const { crumbs } = payload
      state.crumbs = state.crumbs.concat(crumbs)
    },
    removeCrumbs(state, { payload }: PayloadAction<Crumb[]>) {
      const labels = payload.map(e => e.label)
      state.crumbs = state.crumbs.filter(e => !labels.includes(e.label))
    },
    showMobileMenu(state, { payload }: PayloadAction<MobileMenu>) {
      state.mobileMenu = payload
      document.body.classList.add('no-scroll')
    },
    hideMobileMenu(state) {
      state.mobileMenu = null
      document.body.classList.remove('no-scroll')
    },
    setScreeningStatus(state, { payload }: PayloadAction<boolean>) {
      state.isScreeningRequested = payload
    },
    setScreening(state, { payload }: PayloadAction<Screening | null>) {
      if (payload) {
        state.screening.date = payload.date
        state.screening.users = payload.users
      } else {
        state.screening.date = initialState.screening.date
        state.screening.users = initialState.screening.users
      }
    },
    setCurrentTenant(state, { payload }: PayloadAction<string | null>) {
      state.tenant = payload
    },
    setRefreshPage(state, { payload }: PayloadAction<string | null>) {
      state.refetchPage = payload
    },
  },
})

export const screeningRequest = createAction<boolean | undefined>('auth/screeningRequest')

export const {
  showDialog,
  hideDialog,
  formProcessing,
  hideNotification,
  showWarning,
  showError,
  showSuccess,
  clearLoading,
  dialogProcessing,
  hideAllDialogs,
  setCrumbs,
  removeCrumbs,
  minimizeDialog,
  setIsUploading,
  showMinimizeDialog,
  updateDialogProps,
  showMobileMenu,
  hideMobileMenu,
  setScreeningStatus,
  setScreening,
  setCurrentTenant,
  setRefreshPage,

  showDrawer,
  hideDrawer,
  drawerProcessing,
  hideAllDrawers,
  updateDrawerProps,
} = app.actions

export default app.reducer
